<template>
  <nav id="navigation">
    <navigation-main-toggle-button
      v-bind:is-active="isVisible"
      v-on:toggle-main-navigation="toggleMainNavigation()"
    ></navigation-main-toggle-button><!--
 --><navigation-main-logo-button
      v-on:hide-main-navigation="hideMainNavigation()"
    ></navigation-main-logo-button><!--
 --><ul
      id="main-navigation"
      v-bind:class="{ visible: isVisible }"
    >
      <li
        v-for="section in navigation"
        v-bind:key="section.alias"
        v-on:mouseenter="showSubNavigationForLargeScreens(section)"
        v-on:mouseleave="hideSubNavigationForLargeScreens(section)"
      >
        <router-link
          v-bind:to="`/${section.alias}`"
          v-bind:class="sectionToggleClass(section)"
          v-bind:event="supressClickEventForSmallScreens(section)"
          v-on:click.native="toggleSubNavigationOrFollowTheLink(section)"
        ><!--
       -->{{ section.short_name }}<!--
     --></router-link>
        <navigation-main-sub-level
          v-if="areThereCategoriesInSection(section)"
          v-bind:section="section"
          v-bind:is-visible="isSubLevelVisible(section)"
          v-on:hide-main-navigation="hideMainNavigation()"
        ></navigation-main-sub-level>
      </li>
    </ul>
  </nav>
</template>

<script>
import NavigationMainSubLevel from './navigation-main/NavigationMainSubLevel'
import NavigationMainLogoButton from './navigation-main/NavigationMainLogoButton'
import NavigationMainToggleButton from './navigation-main/NavigationMainToggleButton'
import styles from '../../styles/_variables.scss'

export default {
  name: 'NavigationMain',
  props: ['navigation'],
  components: {
    NavigationMainSubLevel,
    NavigationMainLogoButton,
    NavigationMainToggleButton
  },
  data () {
    return {
      isVisible: false,
      areSubLevelsVisible: {},
      desktopBreakpoint: parseInt(styles.desktopMediaBreakpoint)
    }
  },
  watch: {
    navigation: function (nav) {
      let initialValues = {}
      nav.forEach(section => { initialValues[section.alias] = false })
      this.areSubLevelsVisible = Object.assign({}, initialValues)
    }
  },
  methods: {
    areThereCategoriesInSection: function (section) {
      return section.categories && (section.categories.length > 0)
    },
    isSubLevelVisible: function (section) {
      return this.areSubLevelsVisible[section.alias]
    },
    sectionToggleClass: function (section) {
      return {
        'sub-nav-toggle': this.areThereCategoriesInSection(section),
        'sub-nav-expanded': this.isSubLevelVisible(section)
      }
    },

    hideAllSubNavigations: function () {
      this.navigation.forEach(section => {
        this.areSubLevelsVisible[section.alias] = false
      })
    },
    hideMainNavigation: function () {
      this.isVisible = false
      this.hideAllSubNavigations()
    },
    toggleMainNavigation: function () {
      this.isVisible = !this.isVisible
      this.hideAllSubNavigations()
    },

    supressClickEventForSmallScreens: function (section) {
      // follow the link only for large screens. The solution taken from here:
      // https://github.com/vuejs/vue-router/issues/916
      const hasSubNav = this.areThereCategoriesInSection(section)
      const isSmallScreen = (window.innerWidth <= this.desktopBreakpoint)
      return (hasSubNav && isSmallScreen) ? '' : 'click'
    },
    toggleSubNavigationOrFollowTheLink: function (section) {
      if (this.areThereCategoriesInSection(section)) {
        // toggle the isVisible property of SubNavigation
        this.areSubLevelsVisible[section.alias] = !this.areSubLevelsVisible[section.alias]
      } else {
        // no sub-nav means following the link and hiding the main nav
        this.toggleMainNavigation()
      }
    },
    setSubNavigationVisibilityForLargeScreens: function (section, isVisible) {
      const hasSubNav = this.areThereCategoriesInSection(section)
      const isLargeScreen = (window.innerWidth > this.desktopBreakpoint)
      if (hasSubNav && isLargeScreen) {
        const isAnyVisible = Object.values(this.areSubLevelsVisible).some(x => x)
        // do not show sub-level if any is already visible (this may rarely
        // happen in view of transition time lags and a common space
        // shared by all sub-levels on large screens)
        if (!(isAnyVisible && isVisible)) {
          this.areSubLevelsVisible[section.alias] = isVisible
        }
      }
    },
    hideSubNavigationForLargeScreens: function (section) {
      this.setSubNavigationVisibilityForLargeScreens(section, false)
    },
    showSubNavigationForLargeScreens: function (section) {
      this.setSubNavigationVisibilityForLargeScreens(section, true)
    }
  }
}
</script>

<style lang="scss">
  $main-navigation-item-mobile-height: 90px;

  #navigation {
    height: $the-header-main-height;
    @include media(">desktop") { font-size: 14px; };
    @include media("<=desktop") { font-size: 16px; };

    * { box-sizing: border-box; }
    a {
      display: inline-block;
      height: 100%;
    }
  }

  #main-navigation {
    display: inline-block;
    margin: 0;
    letter-spacing: 0.1em;

    @include media(">desktop") {
      overflow-x: auto;
      overflow-y: hidden;
      height: $the-header-main-height;
      white-space: nowrap;
      max-width: calc(100% - 110px);

      @supports #{'\selector(*::-webkit-scrollbar)'} {
        &::-webkit-scrollbar { height: 5px; }
        &::-webkit-scrollbar-track {
          border-radius: 5px;
          background-color: transparent;
        }
        &::-webkit-scrollbar-thumb {
          border-radius: 5px;
          background-color: #222;
          &:hover { background-color: #333; }
        }
      }
      @supports #{'(not \selector(*::-webkit-scrollbar))'} {
        scrollbar-color: #222 transparent;
        scrollbar-width: thin;
      }
    }
    @include media("<=desktop") {
      position: fixed;
      top: $the-header-main-height;
      left: 0;
      max-height: 0;
      overflow-y: auto;
      width: 100%;
      background-color: rgba(0, 0, 0, 0.7);
      transition: max-height 0.5s ease;
    }

    & > li > a {
      letter-spacing: 0.25em;
      line-height: $the-header-main-height;
      height: $the-header-main-height;  /* for border-bottom */
      text-transform: uppercase;

      @include media(">desktop") { padding: 0 0.7em; };
      @include media("<=desktop") {
        line-height: $main-navigation-item-mobile-height;
        height: $main-navigation-item-mobile-height;
        width: 100%;
        box-shadow: 0 0 4em 0.5em rgba(0, 0, 0, 0.9) inset;
        border-bottom: 1px solid black;
      };

      &:hover {
        @include media(">desktop") {
          border-bottom: 1px solid $active-link-color;
        };
      }
    }

    &.visible {
      @include media("<=desktop") {
        max-height: calc(100vh - #{$the-header-main-height});
      };
    }

    & > li {
      @include media("<=desktop") { display: block; };

      & > .sub-nav-toggle {
        &:before {
          @include media("<=desktop") {
            content: "☰";
            padding-right: 7px;
          };
        }
        &:after {
          @include media("<=desktop") {
            content: "+";
            padding-left: 7px;
          };
        }
        &.sub-nav-expanded:after {
          @include media("<=desktop") { content: "–"; };
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/the-header/NavigationMain.vue